import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import FooterSection from "../../components/pages/home/footer-section";
import NavbarSection from "../../components/pages/home/navbar-section";
import PageMeta from "../../components/common/page-meta";
import { getThoughtBySlug } from "../../data/firebase";
import { ThoughtPost } from "../../data/thoughts/props";
import { isInstagramMobileWebView } from "../../utils/instagram-browser";
import { refreshPage } from "../../utils/refresh-page";
import {
  ReadingArticle,
  ReadingBack,
  ReadingBody,
  ReadingContent,
  ReadingDate,
  ReadingDescription,
  ReadingFooter,
  ReadingFocus,
  ReadingHeader,
  ReadingImage,
  ReadingMessage,
  ReadingMetaRow,
  ReadingPage,
  ReadingRefresh,
  ReadingShare,
  ReadingShareFooter,
  ReadingTitle,
} from "./style";

interface ThoughtRouteParams {
  slug: string;
}

const formatDate = (value: string) =>
  new Intl.DateTimeFormat("en", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(value));

const Thought = () => {
  const { slug } = useParams<ThoughtRouteParams>();
  const history = useHistory();
  const [thought, setThought] = useState<ThoughtPost | null>(null);
  const [loading, setLoading] = useState(true);
  const [shareLabel, setShareLabel] = useState("share ↗");
  const showInstagramHelp = isInstagramMobileWebView();

  useEffect(() => {
    getThoughtBySlug(slug).then((post) => {
      setThought(post && post.published ? post : null);
      setLoading(false);
    });
  }, [slug]);

  const renderBody = (body: string) =>
    body
      .split(/\n\s*\n/)
      .filter(Boolean)
      .map((paragraph, index) => <p key={index}>{paragraph}</p>);

  const getThoughtUrl = () => {
    const thoughtUrl = new URL(window.location.href);
    thoughtUrl.searchParams.delete("refresh");
    return thoughtUrl.toString();
  };

  const copyThoughtLink = async () => {
    const thoughtUrl = getThoughtUrl();

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(thoughtUrl);
      return;
    }

    const linkField = document.createElement("textarea");
    linkField.value = thoughtUrl;
    linkField.style.position = "fixed";
    linkField.style.opacity = "0";
    document.body.appendChild(linkField);
    linkField.select();
    document.execCommand("copy");
    document.body.removeChild(linkField);
  };

  const shareThought = async () => {
    if (!thought) {
      return;
    }

    try {
      if (navigator.share) {
        await navigator.share({
          title: thought.title,
          text: thought.description,
          url: getThoughtUrl(),
        });
        return;
      }

      await copyThoughtLink();
      setShareLabel("link copied ✓");
      window.setTimeout(() => setShareLabel("share ↗"), 2400);
    } catch (error: any) {
      if (error?.name === "AbortError") {
        return;
      }

      try {
        await copyThoughtLink();
        setShareLabel("link copied ✓");
        window.setTimeout(() => setShareLabel("share ↗"), 2400);
      } catch (copyError) {
        setShareLabel("try again");
      }
    }
  };

  const leaveReadingView = () => {
    if (window.matchMedia("(min-width: 769px)").matches) {
      history.push("/thoughts");
    }
  };

  return (
    <ReadingPage>
      <PageMeta
        title={thought ? `${thought.title} — Freddy's` : "Thought — Freddy's"}
        description={
          thought?.description ||
          "A personal note from Freddy's writing archive."
        }
      />
      <NavbarSection percentage={0} showLine={false} />
      <ReadingFocus onClick={leaveReadingView}>
        <ReadingArticle onClick={(event) => event.stopPropagation()}>
          <ReadingContent>
            <ReadingBack as={Link} to="/thoughts">
              ← Thoughts
            </ReadingBack>
            {loading && (
              <ReadingMessage>
                {showInstagramHelp
                  ? "Instagram may load this writing slowly. For a better experience, open it in an external browser. "
                  : "Loading writing… "}
                <ReadingRefresh type="button" onClick={refreshPage}>
                  Refresh
                </ReadingRefresh>
                {showInstagramHelp
                  ? " if needed."
                  : " if it takes too long."}
              </ReadingMessage>
            )}
            {!loading && !thought && (
              <ReadingMessage>
                Writing did not load. {" "}
                <ReadingRefresh type="button" onClick={refreshPage}>
                  Refresh
                </ReadingRefresh>
                {" "}to try again.
              </ReadingMessage>
            )}
            {thought && (
              <>
                <ReadingHeader>
                  <ReadingMetaRow>
                    <ReadingDate>{formatDate(thought.publishedAt)}</ReadingDate>
                    <ReadingShare
                      type="button"
                      onClick={shareThought}
                      aria-live="polite"
                    >
                      {shareLabel}
                    </ReadingShare>
                  </ReadingMetaRow>
                  <ReadingTitle>{thought.title}</ReadingTitle>
                <ReadingDescription>{thought.description}</ReadingDescription>
                </ReadingHeader>
                {thought.imageUrl && (
                  <ReadingImage src={thought.imageUrl} alt={thought.title} />
                )}
                <ReadingBody>{renderBody(thought.body)}</ReadingBody>
                <ReadingShareFooter>
                  <ReadingShare
                    type="button"
                    onClick={shareThought}
                    aria-live="polite"
                  >
                    {shareLabel}
                  </ReadingShare>
                </ReadingShareFooter>
              </>
            )}
          </ReadingContent>
        </ReadingArticle>
      </ReadingFocus>
      <ReadingFooter>
        <FooterSection />
      </ReadingFooter>
    </ReadingPage>
  );
};

export default Thought;
